import { KNOP_IN_KOP } from "@/components/rust/Rust";
import React from "react";
import { Link } from "react-router-dom";
import { SectieBalk, BesluitChip, PuntRegel, AfdelingMeldtRegel } from "./VandaagParts";
import { CEL } from "@/components/overzicht/Strook";

export function useBreedte() {
  const ref = React.useRef(null);
  const [w, setW] = React.useState(0);
  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    const meet = () => setW(Math.floor(el.getBoundingClientRect().width));
    meet();
    const ro = new ResizeObserver(meet);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return [ref, w];
}

const komma = (x) => String(x).replace(".", ",");
const weken = (n) => `${n} ${n === 1 ? "week" : "weken"}`;
const opsomming = (xs) => (xs.length <= 1 ? xs.join("") : `${xs.slice(0, -1).join(", ")} en ${xs[xs.length - 1]}`);
const naam = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

const BLOK = { groen: ["op-koers", "groen"], rood: ["rood", "rood"], zonder: ["onvolledig", "onvolledig (cijfers of WOW)"], niet: ["geen-call", "niet gebeld"] };

// v14 F2: de week in één zin en één blokje per locatie (kleuren als in de strook).
export function WeekKaart({ trend, blokken }) {
  const tel = (s) => blokken.filter((b) => b.status === s).length;
  return (
    <div data-testid="vandaag-ceo-week" className="bg-white border border-zinc-200 rounded-[10px] px-4 py-3 mb-4">
      {trend && <h2 data-testid="vandaag-trend" className="text-base font-semibold text-zinc-900">{trend}</h2>}
      <div data-testid="vandaag-ceo-blokken" className="flex gap-[3px] mt-3 w-full overflow-hidden">
        {blokken.map((b) => (
          <span key={b.locatie} title={`${b.locatie}: ${BLOK[b.status][1]}`} data-testid={`ceo-blok-${b.locatie}`} data-status={b.status}
            className={`block flex-1 min-w-0 max-w-[32px] h-6 rounded-[3px] ${CEL[BLOK[b.status][0]]}`} />
        ))}
      </div>
      <p data-testid="vandaag-ceo-blokken-legenda" className="font-mono text-xs text-zinc-500 mt-2">
        {tel("groen")} groen · {tel("rood")} rood · {tel("zonder")} onvolledig (cijfers of WOW) · {tel("niet")} niet gebeld
      </p>
    </div>
  );
}

const ReactieGroep = ({ n, testid }) => {
  const wie = opsomming(n.door.map(naam));
  const zin = n.door.length > 1 ? `${wie} reageerden op` : n.aantal > 1 ? `${wie} reageerde ${n.aantal} keer op` : `${wie} reageerde op`;
  return (
    <Link to={`/afspraken?select=${n.afspraakId}`} data-testid={testid} className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5 flex-1 min-w-0 text-zinc-700 hover:text-blue-700">
      <span className="min-w-0 break-words"><span className="font-semibold">{zin}</span> "{n.wat}"</span>
      {n.besluit && <BesluitChip />}
      <span className="text-xs text-zinc-500">{n.intern ? "Intern" : n.locatie || "Algemeen"}</span>
      {n.laatstLabel && <span className="font-mono text-xs text-zinc-400 ml-auto">laatst {n.laatstLabel}</span>}
    </Link>
  );
};

const RoodRegel = ({ n, onOpen }) => (
  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 flex-1 min-w-0" data-testid={`voor-jou-rood-${n.locatie}`}>
    <span className="inline-block h-2 w-2 rounded-full bg-rose-500 shrink-0" aria-hidden="true" />
    <span className="font-semibold text-zinc-900">{n.locatie}</span>
    <span className="text-zinc-700 flex-1 min-w-[160px]">{n.reden}</span>
    <button type="button" data-testid={`voor-jou-dossier-${n.locatie}`} onClick={() => onOpen(n.locatie)}
      className="min-h-[36px] px-3 rounded-md border border-zinc-300 bg-white text-xs font-semibold text-zinc-800 hover:bg-zinc-50 transition-colors">Dossier</button>
  </div>
);

// v14 F4: één lijst — rood zonder afspraak, reacties per afspraak, punten van de locatie.
export function VoorJou({ items, onOpen, onGezien }) {
  const wisbaar = items.some((n) => n.soort !== "rood");
  return (
    <div className="mb-3 md:mb-4" data-testid="vandaag-voor-jou">
      <SectieBalk testid="vandaag-sectie-voor-jou" titel="Voor jou" aantal={items.length}
        actie={wisbaar ? <button type="button" data-testid="vandaag-reacties-gezien" onClick={onGezien} className={KNOP_IN_KOP}>Gezien</button> : null} />
      {items.length === 0 ? <p data-testid="voor-jou-leeg" className="text-sm text-zinc-500 bg-white border border-zinc-200 rounded-[10px] px-4 py-3">Niets dat op jou wacht.</p> : (
        <ul className="bg-white border border-zinc-200 rounded-[10px] divide-y divide-zinc-200">
          {items.map((n, i) => (
            <li key={`${n.soort}-${n.afspraakId || n.locatie}-${i}`} className="px-3 py-[9px] text-[13px] flex">
              {n.soort === "rood" ? <RoodRegel n={n} onOpen={onOpen} />
                : n.soort === "punt" ? <PuntRegel n={n} testid={`vandaag-punt-${i}`} />
                : n.soort === "afdeling-voortgang" ? <AfdelingMeldtRegel n={n} testid={`vandaag-afdeling-meldt-${i}`} />
                : n.soort === "reactie" ? <ReactieGroep n={n} testid={`vandaag-reactie-${i}`} /> : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

const GROEN = ["#a7f3d0", "#34d399"], GRIJS = ["#e4e4e7", "#d4d4d8"];

function Kolom({ b, x, bw, y, H, onder }) {
  const telt = b.genoegCalls !== false;
  const niet = (b.geenCall || 0) + (b.nogTeBellen || 0);
  const top = telt ? y(b.opKoers) : y(0) - 4;
  return (
    <g data-testid={`groen-kolom-${b.week}`} data-op-koers={b.opKoers} data-genoeg={String(b.genoegCalls)} data-gebeld={b.gebeld} opacity={b.lopend ? 0.5 : 1}>
      <title>{`Over week ${b.overWeekNr} (call ${b.callLabel}): groen ${b.opKoers} · rood ${b.rood} · onvolledig (cijfers of WOW) ${b.zonderCijfers} · niet gebeld ${niet} · gebeld ${b.gebeld} van ${b.locaties}${telt ? "" : " · te weinig calls in de app"}${b.lopend ? " · loopt nog" : ""}`}</title>
      <rect x={x} y={top} width={bw} height={Math.max(0, y(0) - top)} fill={telt ? GROEN[0] : GRIJS[0]} stroke={telt ? GROEN[1] : GRIJS[1]} strokeWidth="1" />
      {telt && <text x={x + bw / 2} y={top - 4} textAnchor="middle" fontSize="11" fontWeight="700" fontFamily="JetBrains Mono, monospace" fill="#18181b">{b.opKoers}</text>}
      <text x={x + bw / 2} y={H - onder + 13} textAnchor="middle" fontSize="10" fontFamily="JetBrains Mono, monospace" fill="#52525b">{b.overWeekNr}</text>
      <text x={x + bw / 2} y={H - onder + 25} textAnchor="middle" fontSize="9" fontFamily="JetBrains Mono, monospace" fill="#a1a1aa">{b.gebeld}</text>
      {b.lopend && <text x={x + bw / 2} y={H - onder + 37} textAnchor="middle" fontSize="9" fill="#71717a">loopt</text>}
    </g>
  );
}

export const gemiddeldeZin = (d) => {
  const n = d.groenGemiddeldeWeken + d.wekenWeinigCalls;
  if (d.groenGemiddelde == null) return `In geen van de laatste ${weken(n)} stond minstens de helft van de locaties in de app; er is nog geen gemiddelde.`;
  if (!d.wekenWeinigCalls) return `Gemiddeld ${komma(d.groenGemiddelde)} groen over de laatste ${weken(n)}.`;
  const w = d.wekenWeinigCalls;
  return `Gemiddeld ${komma(d.groenGemiddelde)} groen in de ${weken(d.groenGemiddeldeWeken)} met genoeg calls, van de laatste ${n}. In ${weken(w)} stond minder dan de helft van de locaties in de app; ${w === 1 ? "die telt" : "die tellen"} niet mee.`;
};

// v14 F5: alleen groen per week; een week met te weinig calls is een korte grijze balk zonder getal.
export function GroenPerWeek({ d }) {
  const balken = d.weekstand;
  const [ref, breedte] = useBreedte();
  const smal = breedte > 0 && breedte < 560;
  const H = smal ? 150 : 180, links = 24, boven = 18, onder = 44;
  const plotH = H - boven - onder;
  const max = Math.max(10, Math.ceil(Math.max(0, ...balken.map((b) => b.opKoers)) / 5) * 5);
  const ticks = Array.from({ length: max / 5 + 1 }, (_, i) => i * 5);
  const kol = balken.length ? Math.max(0, breedte - links - 4) / balken.length : 0;
  const bw = Math.max(8, Math.min(28, kol * 0.62));
  const y = (v) => boven + plotH - (v / max) * plotH;
  return (
    <div className="bg-white border border-zinc-200 rounded-[10px] p-3" data-testid="groen-per-week-kader">
      <div ref={ref} className="w-full">
        {breedte > 0 && (
          <svg data-testid="groen-per-week-grafiek" width={breedte} height={H} className="block" role="img" aria-label="Groen per week over 13 weken">
            {ticks.map((t) => (
              <g key={t}>
                <line x1={links} x2={breedte - 4} y1={y(t)} y2={y(t)} stroke={t === 0 ? "#d4d4d8" : "#f4f4f5"} />
                <text x={links - 6} y={y(t) + 3} textAnchor="end" fontSize="10" fontFamily="JetBrains Mono, monospace" fill="#a1a1aa">{t}</text>
              </g>
            ))}
            {balken.map((b, i) => <Kolom key={b.week} b={b} x={links + i * kol + (kol - bw) / 2} bw={bw} y={y} H={H} onder={onder} />)}
          </svg>
        )}
      </div>
      <p data-testid="groen-gemiddelde-zin" className="text-sm text-zinc-700 mt-2">{gemiddeldeZin(d)}</p>
      <div data-testid="groen-per-week-legenda" className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-zinc-500 mt-2">
        {[[GROEN, "locaties groen"], [GRIJS, "te weinig calls in de app"]].map(([k, t]) => (
          <span key={t} className="inline-flex items-center gap-1"><span className="inline-block h-3 w-3" style={{ background: k[0], border: `1px solid ${k[1]}` }} />{t}</span>
        ))}
        <span className="font-mono">onder de kolom: de week waar de calls over gingen, en hoeveel locaties gebeld zijn</span>
      </div>
    </div>
  );
}

export const vakerRoodZin = (v) => {
  if (!v.aantal) return "Geen locatie komt terug als rood.";
  const gelijk = v.top.every((t) => t.roodAantal === v.top[0].roodAantal);
  const top = gelijk ? `${opsomming(v.top.map((t) => t.locatie))} (${v.top[0].roodAantal} keer)` : opsomming(v.top.map((t) => `${t.locatie} (${t.roodAantal})`));
  return `${v.aantal} van ${v.totaal} locaties ${v.aantal === 1 ? "komt" : "komen"} terug als rood (twee weken op rij, of 4 van de laatste 13). Het vaakst: ${top}.`;
};

// v14 F6: terugkerend rood in één regel.
export const VakerRood = ({ v }) => (
  <div data-testid="vandaag-vaker-rood" className="bg-white border border-zinc-200 rounded-[10px] px-4 py-3 mb-3 md:mb-4 text-sm text-zinc-700">
    <span data-testid="vaker-rood-zin">{vakerRoodZin(v)}</span>
    {v.aantal > 0 && <> <Link to="/overzicht#terugkerend" data-testid="vaker-rood-link" className="font-semibold text-blue-700 hover:text-blue-900">Bekijk in het Overzicht</Link></>}
  </div>
);
